import { useEffect, useState } from 'react';
import { Alert, Box, Button, FormControlLabel, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import NodeAPI from '../api';
import { pushSupported, subscribeDevice, unsubscribeDevice } from '../utils/pushSubscription';
const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
export default function QuizNotificationSettings() {
  const [settings, setSettings] = useState({ enabled: false, preferredLocalTime: '19:00',
    timeZone: localZone, previewMode: 'generic', questionsPerDay: 5 });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [deviceSubscribed, setDeviceSubscribed] = useState(false);
  useEffect(() => {
    if (pushSupported()) navigator.serviceWorker.ready.then(registration =>
      registration.pushManager.getSubscription().then(subscription => setDeviceSubscribed(Boolean(subscription)))).catch(() => {});
    NodeAPI.get('/quiz/notifications/preferences', { params: { timeZone: localZone } }).then(({ data }) => setSettings(data))
      .catch(() => setError('Could not load notification settings.'));
  }, []);
  const save = async next => {
    const { data } = await NodeAPI.put('/quiz/notifications/preferences', next);
    setSettings(data);
  };
  const enable = async () => {
    setBusy(true); setError('');
    try {
      await subscribeDevice();
      setDeviceSubscribed(true);
      await save({ ...settings, enabled: true });
    } catch (e) { setError(e.message || 'Could not enable notifications.'); }
    finally { setBusy(false); }
  };
  const removeDevice = async () => {
    setBusy(true); setError('');
    try { await unsubscribeDevice(); setDeviceSubscribed(false); }
    catch (e) { setError(e.message || 'Could not remove this device.'); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true); setError('');
    try {
      await save({ ...settings, enabled: false });
      if (deviceSubscribed) { await unsubscribeDevice(); setDeviceSubscribed(false); }
    } catch (e) { setError(e.message || 'Could not disable notifications.'); }
    finally { setBusy(false); }
  };
  const update = async changes => {
    const next = { ...settings, ...changes };
    setSettings(next);
    if (next.enabled) {
      try { await save(next); } catch { setError('Could not save notification settings.'); }
    }
  };
  return <Box sx={{ mt: 4 }}>
    <Typography variant="h6">Quiz Notifications</Typography>
    <Typography variant="body2" color="text.secondary">One daily quiz from your saved questions. Save a quiz first to receive questions. The preview can appear on your lock screen.</Typography>
    <Typography sx={{ my: 1 }}>Account: {settings.enabled ? 'Enabled' : 'Disabled'} · This device: {deviceSubscribed ? 'Subscribed' : 'Not subscribed'}</Typography>
    {!pushSupported() && <Alert severity="info">Push is unavailable here. On iPhone or iPad, add this app to the Home Screen first.</Alert>}
    {typeof Notification !== 'undefined' && Notification.permission === 'denied' &&
      <Alert severity="warning">Notifications are blocked. Enable them in browser or system settings.</Alert>}
    <TextField type="number" label="Questions per day" value={settings.questionsPerDay}
      inputProps={{ min: 1, max: 20, step: 1 }}
      onChange={e => update({ questionsPerDay: Number(e.target.value) })} sx={{ mt: 2, mr: 2, width: 170 }} />
    <TextField type="time" label="Notification time" value={settings.preferredLocalTime}
      onChange={e => update({ preferredLocalTime: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ mt: 2, mr: 2 }} />
    <TextField label="Time zone" value={settings.timeZone}
      onChange={e => setSettings({ ...settings, timeZone: e.target.value })}
      onBlur={() => settings.enabled && save(settings).catch(() => setError('Invalid time zone or settings could not be saved.'))}
      helperText="IANA zone, for example Europe/London" sx={{ mt: 2 }} />
    <Typography sx={{ mt: 2 }}>Notification preview</Typography>
    <RadioGroup value={settings.previewMode} onChange={e => update({ previewMode: e.target.value })}>
      <FormControlLabel value="generic" control={<Radio />} label="Generic" />
      <FormControlLabel value="question" control={<Radio />} label="Show first question when suitable" />
    </RadioGroup>
    {error && <Alert severity="error">{error}</Alert>}
    {!deviceSubscribed && <Button disabled={busy || !pushSupported()} onClick={enable}>Enable Quiz Notifications on this device</Button>}
    {deviceSubscribed && <Button disabled={busy} onClick={removeDevice}>Remove this device</Button>}
    {settings.enabled && <Button disabled={busy} onClick={disable}>Disable all Quiz Notifications</Button>}
  </Box>;
}
